import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import debounce from 'lodash/debounce'
import uniqueId from 'lodash/uniqueId'

import Button from '@react/react-spectrum/Button'
import Select from '@react/react-spectrum/Select/js/Select'
import { Table, TBody, TD, TR } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import ArrowUp from '@react/react-spectrum/Icon/ArrowUp'
import ArrowDown from '@react/react-spectrum/Icon/ArrowDown'

import Add from '@react/react-spectrum/Icon/Add'
import Delete from '@react/react-spectrum/Icon/Delete'

import './Multifield.scss'

let shouldSave = false

export default function Multifield({
  type,
  options,
  renderItem,
  allowDuplicates,
  limit,
  maxHeight,
  disabled,
  value,
  onChange,
}) {
  const [items, setItems] = useState([])
  const [newItem, setNewItem] = useState(false)
  const uuid = useRef(uniqueId('Multifield_'))
  const debouncedOnChange = useRef()

  const values = useMemo(
    () =>
      (Array.isArray(value) ? value : !!value ? [value] : []).filter((v) =>
        options?.length
          ? options.findIndex((opt) => {
              const _v = typeof v === 'string' ? v : v?.fullValue || v?.value
              return opt.value === _v
            }) > -1
          : true
      ),
    [value, options]
  )

  useEffect(() => {
    if (!shouldSave && !items?.length && values.length) {
      setItems(values.map((value) => ({ id: uniqueId('Multifield__value_'), value })))
    }
  }, [values, items])

  useEffect(() => {
    if (shouldSave) {
      shouldSave = false

      // with text Multifield execute onChange only when the user stops typing
      if (type.startsWith('text')) {
        if (debouncedOnChange.current) {
          debouncedOnChange.current.cancel()
        }
        const mappedItems = items.map(({ value }) => value)
        debouncedOnChange.current = debounce(() => onChange(mappedItems), 500)
        debouncedOnChange.current()
      } else {
        onChange(items.map(({ value }) => value))
      }
    }
  }, [items])

  const getOptions = useCallback(
    (item) =>
      options?.filter(
        (option) =>
          option.value === item?.value || allowDuplicates || !items.some(({ value }) => value === option.value)
      ) || [],
    [allowDuplicates, items, options]
  )

  const addItem = useCallback(
    (v) => {
      shouldSave = true
      setItems((items) => [...items, { id: uniqueId('Multifield__value_'), value: v }])
      setNewItem(null)
    },
    [setItems, setNewItem]
  )

  const debouncedAddItem = useCallback(
    debounce(function (v) {
      shouldSave = true
      setItems((items) => [...items, { id: uniqueId('Multifield__value_'), value: v }])
      setNewItem(null)
    }, 1000),
    [setItems, setNewItem]
  )

  const onItemUpdate = (idx) => (v) => {
    if (idx < 0) {
      if (type.startsWith('text')) debouncedAddItem(v)
      else addItem(v)
    } else {
      let newItems = [...items]
      newItems[idx].value = v
      shouldSave = true
      setItems(newItems)
    }
  }

  const onRemoveItem = (idToRemove) => () => {
    shouldSave = true
    setItems((items) => items.filter(({ id }) => id !== idToRemove))
  }

  const attrs = maxHeight ? { style: { maxHeight } } : {}
  const onMoveItem = (idx, direction) => {
    shouldSave = true
    setItems((prevItems) => {
      const newItems = [...prevItems]
      const swapWith = direction === 'up' ? idx - 1 : idx + 1

      if (swapWith < 0 || swapWith >= newItems.length) return prevItems

      // swap
      const temp = newItems[swapWith]
      newItems[swapWith] = newItems[idx]
      newItems[idx] = temp

      return newItems
    })
  }

  const canAddItem = !disabled && !newItem && (options ? getOptions().length : true) && items?.length < (limit || 100)

  return (
    <div className='multifield' {...attrs}>
      <Table>
        <TBody>
          {items.map(({ id, value }, idx) => (
            <TR key={`${uuid.current}-${id}`}>
              <TD>
                <Input
                  item={
                    typeof value === 'string' || typeof value === 'number'
                      ? value
                      : value?.fullValue || value?.value || ''
                  }
                  type={type}
                  options={getOptions({ id, value })}
                  onChange={onItemUpdate(idx)}
                  disabled={disabled}
                  renderItem={renderItem}
                />
              </TD>
              <TD width='50px'>
                <Button
                  variant='tool'
                  icon={<ArrowUp size='S' />}
                  onClick={() => onMoveItem(idx, 'up')}
                  disabled={disabled || idx === 0}
                />
                <Button
                  variant='tool'
                  icon={<ArrowDown size='S' />}
                  onClick={() => onMoveItem(idx, 'down')}
                  disabled={disabled || idx === items.length - 1}
                />
                <Button variant='tool' icon={<Delete size='S' />} onClick={onRemoveItem(id)} disabled={disabled} />
              </TD>
            </TR>
          ))}
          {newItem && (
            <TR>
              <TD>
                <Input
                  item={null}
                  type={type}
                  options={getOptions(null)}
                  onChange={onItemUpdate(-1)}
                  renderItem={renderItem}
                  disabled={disabled}
                />
              </TD>
              <TD width='50px'>
                <Button variant='tool' icon={<Delete />} onClick={() => setNewItem(false)} disabled={disabled} />
              </TD>
            </TR>
          )}
          <TR onClick={() => canAddItem && setNewItem(true)}>
            <TD>
              <Button quiet variant='action' icon={<Add />} disabled={!canAddItem}>
                Add
              </Button>
            </TD>
            <TD />
          </TR>
        </TBody>
      </Table>
    </div>
  )
}

function Input({ item, options, onChange, disabled, type, renderItem }) {
  switch (type) {
    case 'select':
    case 'dropdown':
    case 'picker':
      return <Select value={item} renderItem={renderItem} options={options} onSelect={onChange} disabled={disabled} />
    case 'text':
    default:
      return <Textfield defaultValue={item || ''} onChange={onChange} disabled={disabled} />
  }
}

Multifield.defaultProps = {
  options: null,
  allowDuplicates: false,
  limit: 100,
  disabled: false,
  type: 'text',
  uuid: '',
}
